/**
 * Client-side parsers for admin bulk import (categories + slide metadata).
 */

function splitLine(line) {
    const t = line.trim();
    if (!t) return [];
    if (line.includes('\t')) {
        return line.split('\t').map((s) => s.trim());
    }
    // naive CSV: comma split, strip quotes
    return line.split(',').map((s) => {
        let x = s.trim();
        if (x.startsWith('"') && x.endsWith('"')) x = x.slice(1, -1);
        return x.trim();
    });
}

/**
 * @returns {{ id: string, label: string }[]}
 */
export function parseCategoryPaste(text) {
    const lines = String(text ?? '').split(/\r?\n/);
    const out = [];
    const seen = new Set();
    for (const line of lines) {
        const parts = splitLine(line);
        if (parts.length === 0) continue;
        const id = parts[0];
        if (!id) continue;
        if (seen.has(id)) continue;
        seen.add(id);
        const label = parts.length > 1 ? parts.slice(1).join(' ').trim() : id;
        out.push({ id, label: label || id });
    }
    return out;
}

/**
 * @param {string} text - full file text
 * @returns {{ id: string, label: string }[]}
 */
export function parseCategoryCsvText(text) {
    const lines = String(text ?? '').split(/\r?\n/).filter((l) => l.trim());
    if (lines.length === 0) return [];
    const header = splitLine(lines[0]).map((h) => h.toLowerCase());
    let idIdx = header.indexOf('id');
    let labelIdx = header.indexOf('label');
    if (idIdx < 0 && header.length >= 1) idIdx = 0;
    if (labelIdx < 0 && header.length >= 2) labelIdx = 1;
    if (idIdx < 0) {
        throw new Error('CSV must include an "id" column or use the first column as id.');
    }
    const out = [];
    const seen = new Set();
    for (let i = 1; i < lines.length; i++) {
        const parts = splitLine(lines[i]);
        if (parts.length === 0) continue;
        const id = parts[idIdx] ?? '';
        if (!id) continue;
        if (seen.has(id)) continue;
        seen.add(id);
        const label = labelIdx >= 0 ? (parts[labelIdx] ?? id) : id;
        out.push({ id, label: String(label || id) });
    }
    return out;
}

/**
 * Parse TSV/CSV with header row into array of records.
 * @param {string} text
 * @returns {Record<string, string>[]}
 */
export function parseSlideTableText(text) {
    const lines = String(text ?? '').split(/\r?\n/).filter((l) => l.trim());
    if (lines.length < 2) {
        throw new Error('Need a header row and at least one data row.');
    }
    const headers = splitLine(lines[0]).map((h) => h.trim());
    if (!headers.length) throw new Error('Missing header row.');
    const filenameIdx = headers.findIndex(
        (h) => h.toLowerCase() === 'filename' || h.toLowerCase() === 'file' || h.toLowerCase() === 'name',
    );
    const fnCol = filenameIdx >= 0 ? filenameIdx : 0;
    const out = [];
    for (let i = 1; i < lines.length; i++) {
        const parts = splitLine(lines[i]);
        if (parts.length === 0) continue;
        const row = {};
        headers.forEach((h, j) => {
            row[h] = parts[j] ?? '';
        });
        const fn = String(parts[fnCol] ?? '').trim();
        if (!fn) continue;
        row.filename = fn;
        out.push(row);
    }
    if (out.length === 0) throw new Error('No data rows with a filename.');
    return out;
}

export function readFileAsText(file) {
    return new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(String(r.result ?? ''));
        r.onerror = () => reject(new Error('Failed to read file.'));
        r.readAsText(file);
    });
}
